/**
 * Warm the secondary route chunks once the page is idle.
 *
 * The pages are split into separate chunks so the first paint stays small.
 * Prefetching after load keeps navigation instant without slowing down the
 * initial render (search engines measure that part).
 */

const loaders: Array<() => Promise<unknown>> = [
  () => import("@/pages/ki/page"),
  () => import("@/pages/karriere/page"),
  () => import("@/pages/fische/page"),
  () => import("@/pages/impressum/page"),
  () => import("@/pages/datenschutz/page"),
  () => import("@/pages/haftungsausschluss/page"),
];

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
};

function isConstrainedConnection(): boolean {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  if (!connection) return false;
  if (connection.saveData) return true;
  return connection.effectiveType === "slow-2g" || connection.effectiveType === "2g";
}

export function prefetchRoutes(): void {
  if (typeof window === "undefined") return;
  if (isConstrainedConnection()) return;

  const run = () => {
    loaders.forEach((load) => {
      // Fire and forget - failures simply mean the route loads on demand.
      load().catch(() => undefined);
    });
  };

  const start = () => {
    const idleWindow = window as IdleWindow;
    if (typeof idleWindow.requestIdleCallback === "function") {
      idleWindow.requestIdleCallback(run, { timeout: 4000 });
    } else {
      window.setTimeout(run, 1200);
    }
  };

  // Wait until the first paint / LCP window is over before pulling more JS.
  window.setTimeout(start, 2000);
}